import React, { useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Building2, AlertTriangle, ShieldAlert, FileBadge,
  FileText, Map, FolderOpen, Users, LogOut, Menu, X, ChevronRight,
  Wrench, Home as HomeIcon, BookKey, Briefcase, Bell, ShieldCheck, LifeBuoy,
} from "lucide-react";
import { ROLE_LABELS } from "@/lib/dict";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord", roles: "all" },
  { to: "/assets", icon: Building2, label: "Portefeuille / Actifs", roles: ["super_admin", "org_admin", "property_manager", "asset_manager", "technical_manager", "accountant"] },
  { to: "/clients", icon: Briefcase, label: "Clients", roles: ["super_admin", "org_admin", "property_manager", "asset_manager"] },
  { to: "/insurance", icon: ShieldCheck, label: "Attestations d'assurance", roles: ["super_admin", "org_admin", "property_manager", "asset_manager"] },
  { to: "/defects", icon: AlertTriangle, label: "Désordres", roles: ["super_admin", "org_admin", "property_manager", "asset_manager", "technical_manager"] },
  { to: "/claims", icon: ShieldAlert, label: "Sinistres & DO", roles: ["super_admin", "org_admin", "property_manager", "asset_manager", "technical_manager", "accountant", "insurer", "broker", "expert"] },
  { to: "/warranties", icon: FileBadge, label: "Garanties", roles: ["super_admin", "org_admin", "property_manager", "asset_manager", "technical_manager"] },
  { to: "/policies", icon: BookKey, label: "Polices d'assurance", roles: ["super_admin", "org_admin", "property_manager", "asset_manager", "accountant", "broker"] },
  { to: "/plans", icon: Map, label: "Plans interactifs", roles: ["super_admin", "org_admin", "property_manager", "asset_manager", "technical_manager"] },
  { to: "/documents", icon: FolderOpen, label: "Documents", roles: ["super_admin", "org_admin", "property_manager", "asset_manager", "technical_manager", "accountant"] },
  { to: "/tenant", icon: HomeIcon, label: "Portail locataire", roles: ["tenant"] },
  { to: "/contractor", icon: Wrench, label: "Portail entreprise", roles: ["contractor"] },
  { to: "/admin", icon: Users, label: "Administration", roles: ["org_admin", "super_admin"] },
  { to: "/settings/notifications", icon: Bell, label: "Notifications", roles: ["org_admin", "super_admin", "property_manager", "asset_manager", "technical_manager"] },
  { to: "/help-center", icon: LifeBuoy, label: "Centre d'aide", roles: "all" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const allowedNav = NAV.filter((n) => n.roles === "all" || n.roles.includes(user.role));

  const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();

  const SidebarContent = () => (
    <>
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: "#023E73" }}>
            <ShieldAlert size={18} strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-display font-bold text-[15px] leading-tight text-slate-900">Warranty</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">& Risk Manager</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {allowedNav.map((n) => {
          const active = location.pathname.startsWith(n.to);
          const Icon = n.icon;
          return (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.to.slice(1)}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm font-medium transition-all ${
                active
                  ? "text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
              style={active ? { background: "#023E73" } : {}}
            >
              <Icon size={18} strokeWidth={2} />
              <span className="flex-1">{n.label}</span>
              {active && <ChevronRight size={14} />}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-9 w-9">
            <AvatarFallback style={{ background: "#023E73", color: "white" }} className="text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{user.first_name} {user.last_name}</div>
            <div className="text-[11px] text-slate-500 truncate">{ROLE_LABELS[user.role]}</div>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" title="Déconnexion">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 flex-col z-30">
        <SidebarContent />
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="p-2 -ml-2 rounded-md hover:bg-slate-100">
          <Menu size={20} />
        </button>
        <div className="font-display font-bold text-sm">Warranty & Risk Manager</div>
        <div className="ml-auto"><NotificationBell /></div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white flex flex-col">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 p-2 rounded hover:bg-slate-100"><X size={18} /></button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="lg:pl-64 min-h-screen">
        <div className="hidden lg:flex sticky top-0 z-20 bg-slate-50/80 backdrop-blur-sm border-b border-slate-200 px-8 py-2 justify-between items-center gap-4">
          <GlobalSearch />
          <div data-testid="desktop-bell-wrap"><NotificationBell /></div>
        </div>
        <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
